/**
 * RiderPreSettlementHistory Component
 * 라이더 선정산내역 페이지 (본사용)
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import {useRiderPreSettlementData} from '../hooks/useRiderPreSettlementData';
import {useUserInfo} from '@/shared/hooks/useCrudOperations';
import {usePagination} from '@/shared/hooks/usePagination';
import {useColumnSort} from '@/shared/hooks/useTableSort';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import Loading from '@/shared/components/feedback/Loading';
import {loadFilters, saveFilters} from '@/shared/utils/filterStorage';
import {
    calculateInitialDateRange,
    validateFilters,
    getPreSettlementValueByIndex,
} from '../utils/riderPreSettlementHelpers';
import { FilterState, RiderPreSettlement } from '../types/riderPreSettlement.types';
import FilterSection from './FilterSection';
import SettlementTable from './SettlementTable';

import '@/shared/styles/common-table.css';

/** 필터 저장 키 */
const FILTER_STORAGE_KEY = 'riderPreSettlementFilters';

/** 페이지당 아이템 수 */
const ITEMS_PER_PAGE = 10;

const RiderPreSettlementHistory: React.FC = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    // ======== 사용자 정보 ========
    const userInfo = getUserInfo();
    const userType = userInfo?.userType;
    const isSupplierUser = userType === 'SUPPLIER';
    const isRiderUser = userType === 'RIDER';
    const userSupplierId = isSupplierUser ? userInfo?.userId : null;

    // ======== 데이터 훅 ========
    const {
        settlements,
        suppliers,
        latestDate,
        loading,
        refreshSettlements,
        downloadExcel
    } = useRiderPreSettlementData(userSupplierId, true);

    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // ======== 필터 상태 ========
    const [filters, setFilters] = useState<FilterState>(() => {
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);
        return saved ? {
            acctStDate: saved.acctStDate || '',
            acctEdDate: saved.acctEdDate || '',
            riderName: saved.riderName || '',
            supplierId: initialSupplierId
        } : {
            acctStDate: '',
            acctEdDate: '',
            riderName: '',
            supplierId: initialSupplierId
        };
    });

    // ======== 정렬 ========
    const {
        sortConfig,
        sortedData: sortedSettlements,
        handleSort,
        resetSort
    } = useColumnSort<RiderPreSettlement>({
        data: settlements,
        getValueByIndex: getPreSettlementValueByIndex
    });

    // ======== 페이지네이션 ========
    const {
        currentPage,
        totalPages,
        currentItems,
        goToPage,
        resetPagination
    } = usePagination({ items: sortedSettlements, itemsPerPage: ITEMS_PER_PAGE });

    const hasAppliedInitialFilter = useRef(false);

    // ======== Effects ========

    useEffect(() => {
        const filtersToSave = {
            acctStDate: filters.acctStDate,
            acctEdDate: filters.acctEdDate,
            riderName: filters.riderName,
            supplierId: isSupplierUser ? filters.supplierId : ''
        };
        saveFilters(FILTER_STORAGE_KEY, filtersToSave);
    }, [filters, isSupplierUser]);

    useEffect(() => {
        if (hasAppliedInitialFilter.current) return;
        if (loading) return;
        if (!isSupplierUser && suppliers.length === 0 && userSupplierId !== null) return;

        const supplierId = isSupplierUser ? (userSupplierId || '') : '';
        if (isSupplierUser && !supplierId) return;

        hasAppliedInitialFilter.current = true;

        const saved = loadFilters<FilterState>(FILTER_STORAGE_KEY);

        const runInitialFetch = async () => {
            let startDate: string;
            let endDate: string;
            let riderName: string | undefined;

            if (saved?.acctStDate && saved?.acctEdDate) {
                startDate = saved.acctStDate;
                endDate = saved.acctEdDate;
                riderName = saved.riderName || undefined;
                setFilters({ acctStDate: startDate, acctEdDate: endDate, riderName: saved.riderName || '', supplierId });
            } else {
                const dateToUse = latestDate || new Date().toISOString().split('T')[0];
                const range = calculateInitialDateRange(dateToUse);
                startDate = range.startDate;
                endDate = range.endDate;
                riderName = undefined;
                setFilters({ acctStDate: startDate, acctEdDate: endDate, riderName: '', supplierId });
            }

            const count = await refreshSettlements(startDate, endDate, riderName, supplierId || undefined);
            setIsInitialLoading(false);
            if (count === 0) {
                showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
            }
        };

        runInitialFetch();
    }, [loading, suppliers, latestDate, isSupplierUser, userSupplierId, refreshSettlements, showToast]);

    // ======== Handlers ========

    const handleFilterChange = useCallback((key: string, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleSearch = useCallback(async () => {
        const validation = validateFilters(filters.acctStDate, filters.acctEdDate, filters.supplierId);
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        const resultCount = await refreshSettlements(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
        resetPagination();

        if (resultCount === 0) {
            showToast('조회된 정산내역이 없습니다.', { type: ToastType.INFO });
        }
    }, [filters, refreshSettlements, resetPagination, showToast]);

    const handleReset = useCallback(() => {
        const initialSupplierId = isSupplierUser ? (userSupplierId || '') : '';
        const dateToUse = latestDate || new Date().toISOString().split('T')[0];
        const { startDate, endDate } = calculateInitialDateRange(dateToUse);

        setFilters({ acctStDate: startDate, acctEdDate: endDate, riderName: '', supplierId: initialSupplierId });
        resetSort();
        resetPagination();

        if (startDate && endDate) {
            refreshSettlements(startDate, endDate, undefined, initialSupplierId || undefined);
        }
    }, [isSupplierUser, userSupplierId, latestDate, resetPagination, refreshSettlements, resetSort]);

    const handleExcelDownload = useCallback(async () => {
        const validation = validateFilters(filters.acctStDate, filters.acctEdDate, filters.supplierId);
        if (!validation.isValid) {
            showToast(validation.message!, { type: ToastType.WARNING });
            return;
        }

        await downloadExcel(
            filters.acctStDate,
            filters.acctEdDate,
            filters.riderName || undefined,
            filters.supplierId || undefined
        );
    }, [filters, downloadExcel, showToast]);

    // ======== Render ========
    if (loading && !isInitialLoading) {
        return <Loading message="엑셀 다운로드 중..." />;
    }

    if (isInitialLoading) {
        return <Loading message="라이더 선정산내역을 불러오는 중..." />;
    }

    return (
        <div className="supplier-selection-page">
            <div className="page-title-wrapper">
                <h1 className="page-title">라이더 선정산내역</h1>
            </div>

            <FilterSection
                filters={filters}
                suppliers={suppliers}
                isSupplierUser={isSupplierUser}
                onFilterChange={handleFilterChange}
                onSearch={handleSearch}
                onReset={handleReset}
                onExcelDownload={handleExcelDownload}
            />

            <SettlementTable
                settlements={sortedSettlements}
                currentItems={currentItems}
                currentPage={currentPage}
                loading={loading}
                isRiderUser={isRiderUser}
                sortConfig={sortConfig}
                onSort={handleSort}
            />

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default RiderPreSettlementHistory;
